/**
 * An alias: what the Operator's URLs name a dataspace, or a process of one, by instead of its id —
 * `/operator/night-shift/processes/intake`.
 *
 * THE RULE IS THE SERVER'S (`server/src/routes/dataspaces/alias.rs`), and what is stored is what the
 * server makes of what was sent. This copy is for two things only: the hint under a field, which says
 * the URL an alias will make while it is typed, and telling an id from an alias in a URL, which the
 * Operator's route does before it asks the server anything (`isUuid`).
 *
 * - Normalized: trimmed, A to Z lowercased, and every run of spaces, `_` and `-` one `-`, none left at
 *   either end. `My Process`, `my_process` and `MY-PROCESS` are one alias. Nothing left is no alias.
 * - Its form is `a-z`, `0-9` and single inner hyphens, at most 63 characters; never an id's shape, and
 *   never a word the server's routes use in an id's place.
 */

/** The longest alias: one DNS label. */
export const ALIAS_MAX_LENGTH = 63;

/** Words the server's routes use where an id goes (`…/processes/available`, `…/resolve/…`). */
const RESERVED = ["available", "restart-outdated", "resolve"];

/** How the server writes every id it mints: `8-4-4-4-12` hex digits. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const FORM = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Whether a URL's segment is an id. An alias never has this shape, so whatever does not is one. */
export function isUuid(segment: string): boolean {
	return UUID.test(segment);
}

/** `raw` as the server would store it; `""` for no alias. Whether that is an alias is `aliasProblem`'s to say. */
export function normalizeAlias(raw: string): string {
	let alias = "";
	for (const c of raw.trim()) {
		if (c === " " || c === "_" || c === "-") {
			if (alias && !alias.endsWith("-")) alias += "-";
		} else {
			// ASCII only, as the server: a letter it would refuse is not made into one it would take.
			alias += c >= "A" && c <= "Z" ? c.toLowerCase() : c;
		}
	}
	return alias.replace(/-+$/, "");
}

/** Why the server would refuse an alias, the four ways it can: not the form, too long, an id, a route's word. */
export type AliasProblem = "form" | "length" | "id" | "reserved";

/** What is wrong with `alias` — normalized, as `normalizeAlias` returns it — or `null` for nothing, which no alias is either. */
export function aliasProblem(alias: string): AliasProblem | null {
	if (!alias) return null;
	if (!FORM.test(alias)) return "form";
	if (alias.length > ALIAS_MAX_LENGTH) return "length";
	if (isUuid(alias)) return "id";
	if (RESERVED.includes(alias)) return "reserved";
	return null;
}
